import React, { useCallback, useEffect } from 'react';
import { Flex } from '~/ui/flex/Flex';
import { Overlay } from '~/ui/overlay/Overlay';
import { Root } from './Root';
import { DocumentsProvider, useDocumentsStore } from './Store';
import PoliciesIcon from '~/assets/icons/policies.svg';
import styled from '@xstyled/styled-components';
import { t } from 'ttag';
import { Modal } from '~/ui/modal/Modal';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '../state';
import { useOpenCloseAudioEffect } from '~/ui/audio-effect/useOpenCloseAudioEffect';

export const PoliciesButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  border: 1px solid ${p => p.theme.colors.greyMed};
  border-radius: 10px;
  background-color: transparent;
`;

const selector = (s: TrainingState) => select(s, 'policies');

const DocumentsUI = () => {
  const { open, toggle } = useDocumentsStore();
  const { policies } = useTrainingStore(selector);

  useOpenCloseAudioEffect(open);

  const escFunction = useCallback(event => {
    if (event.keyCode === 27) {
      toggle(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', escFunction);

    return () => {
      toggle(false);
      document.removeEventListener('keydown', escFunction);
    };
  }, [escFunction]);

  if (!policies || policies.length === 0) {
    return null;
  }

  return (
    <>
      <PoliciesButton
        onClick={() => toggle(true)}
        aria-label={t`Documents`}
        tabIndex={2}
      >
        <PoliciesIcon />
      </PoliciesButton>
      <Overlay open={open} onClick={() => toggle(false)}>
        <Flex
          zIndex="dialogs"
          p={{ _: 'sm', sm: 'xl' }}
          minHeight="100%"
          justifyContent={{ _: 'stretch', sm: 'center' }}
          alignItems={{ _: 'flex-end', sm: 'center' }}
          overflow="hidden"
          aria-modal={open}
          autoFocus
        >
          <Modal horizontal="center" open={open} vertical="center">
            <Root />
          </Modal>
        </Flex>
      </Overlay>
    </>
  );
};

export const Documents = () => (
  <DocumentsProvider>
    <DocumentsUI />
  </DocumentsProvider>
);
